import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { ReportExtendedConclusion } from '@/shared/api/protocol-ts/model/dto_report_pb';
import {
  useAppDispatch,
  useAppSelector,
  useUserTimezone,
} from '@/shared/hooks';
import { getName } from '@/shared/lib';
import { Button } from '@/shared/ui';

import { patientModel, useFormatPatientDateOfBirth } from '@/entities/patient';
import { reportsModel, useCheckReportSignature } from '@/entities/reports';
import { userModel } from '@/entities/user';

import { FieldConfig, FormValues } from '../../config';
import { selectStudyInfo } from '../../model/extendedConclusion.selectors';
import { ConclusionInfoRow } from '../ConclusionInfoRow/ConclusionInfoRow';

import styles from './ExtendedConclusion.module.scss';

interface ExtendedConclusionProps {
  className?: string;
  isPreview?: boolean;
}

export const ExtendedConclusion: FC<ExtendedConclusionProps> = ({
  className,
  isPreview,
}) => {
  const dispatch = useAppDispatch();
  const { formatMessage, formatDate } = useIntl();

  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);

  const report = useAppSelector(reportsModel.selectors.selectCurrentReport);
  const patient = useAppSelector(patientModel.selectors.selectCurrentPatient);
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const studyMeta = useAppSelector(
    selectStudyInfo(
      // extended conclusion is related to the first source study because
      // it's a single source report and CBCT is a single study type
      report?.SourceStudyIDs?.at(0) ?? '',
    ),
  );

  const patientName = getName(
    patient?.PersonalData?.FirstName,
    patient?.PersonalData?.LastName,
    { userLocale },
  );

  const formattedPatientAge = useFormatPatientDateOfBirth({
    patientDateOfBirth: patient?.PersonalData?.DateOfBirth,
    pluralized: true,
  });

  const userTimezone = useUserTimezone();

  const studyDate = studyMeta?.studyTime || studyMeta?.uploadDate;
  const fov = studyMeta?.fov || '';
  const model = studyMeta?.model || '';

  const studyFormattedDate = formatDate(studyDate, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: userTimezone,
  });

  const reportID = report?.ID || '';
  const extendedConclusion =
    report?.ExtendedConclusion || new ReportExtendedConclusion();

  const { control, handleSubmit, reset, setValue } = useForm<FormValues>({
    defaultValues: {
      Patient: extendedConclusion.Patient || patientName || '',
      DOB: extendedConclusion.DOB || formattedPatientAge || '',
      Address: extendedConclusion.Address || '',
      PrescribingDoctor: extendedConclusion.PrescribingDoctor || '',
      StudyDate: extendedConclusion.StudyDate || studyFormattedDate,
      TypeOfScan: extendedConclusion.TypeOfScan || '',
      FOV: extendedConclusion.FOV || fov || '',
      ScanSource: extendedConclusion.ScanSource || '',
      CBCTModel: extendedConclusion.CBCTModel || model || '',
      IndicationForScan: extendedConclusion.IndicationForScan || '',
      LimitationsFactors: extendedConclusion.LimitationsFactors || '',
      Observations: extendedConclusion.Observations || '',
      Dentition: extendedConclusion.Dentition || '',
      AreaOfInterest: extendedConclusion.AreaOfInterest || '',
      Sinuses: extendedConclusion.Sinuses || '',
      Airway: extendedConclusion.Airway || '',
      Osseous: extendedConclusion.Osseous || '',
      Additional: extendedConclusion.Additional || '',
      Impressions: extendedConclusion.Impressions || '',
    },
  });

  useEffect(() => {
    if (!extendedConclusion.Patient && patientName) {
      setValue('Patient', patientName);
    }
    if (!extendedConclusion.DOB && formattedPatientAge) {
      setValue('DOB', formattedPatientAge);
    }
    if (!extendedConclusion.StudyDate && studyFormattedDate) {
      setValue('StudyDate', studyFormattedDate);
    }
    if (!extendedConclusion.FOV && fov) {
      setValue('FOV', fov);
    }
  }, [
    patientName,
    formattedPatientAge,
    extendedConclusion,
    studyFormattedDate,
    fov,
    model,
    setValue,
  ]);

  const { checkReportSignature } = useCheckReportSignature();

  const handleEditStart = () => {
    reset({
      Patient: extendedConclusion.Patient || patientName || '',
      DOB: extendedConclusion.DOB || formattedPatientAge || '',
      Address: extendedConclusion.Address || '',
      PrescribingDoctor: extendedConclusion.PrescribingDoctor || '',
      StudyDate: extendedConclusion.StudyDate || studyFormattedDate,
      TypeOfScan: extendedConclusion.TypeOfScan || '',
      FOV: extendedConclusion.FOV || fov,
      ScanSource: extendedConclusion.ScanSource || '',
      CBCTModel: extendedConclusion.CBCTModel || model,
      IndicationForScan: extendedConclusion.IndicationForScan || '',
      LimitationsFactors: extendedConclusion.LimitationsFactors || '',
      Observations: extendedConclusion.Observations || '',
      Dentition: extendedConclusion.Dentition || '',
      AreaOfInterest: extendedConclusion.AreaOfInterest || '',
      Sinuses: extendedConclusion.Sinuses || '',
      Airway: extendedConclusion.Airway || '',
      Osseous: extendedConclusion.Osseous || '',
      Additional: extendedConclusion.Additional || '',
      Impressions: extendedConclusion.Impressions || '',
    });
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  const onSubmit = (formData: FormValues) => {
    checkReportSignature({
      onSignatureChecked: async () => {
        setLoading(true);

        try {
          const conclusionData = new ReportExtendedConclusion(formData);

          await dispatch(
            reportsModel.thunks.setReportExtendedConclusion({
              ReportID: reportID,
              Conclusion: conclusionData,
            }),
          );
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
          setIsEditMode(false);
        }
      },
    });
  };

  const patientFields: FieldConfig[] = [
    {
      name: 'Patient',
      label: formatMessage({
        id: 'report.extendedConclusion.patient',
        defaultMessage: 'Patient',
      }),
    },
    {
      name: 'DOB',
      label: formatMessage({
        id: 'report.extendedConclusion.dob',
        defaultMessage: 'DOB',
      }),
    },
    {
      name: 'Address',
      label: formatMessage({
        id: 'report.extendedConclusion.address',
        defaultMessage: 'Address',
      }),
    },
    {
      name: 'PrescribingDoctor',
      label: formatMessage({
        id: 'report.extendedConclusion.prescribingDoctor',
        defaultMessage: 'Prescribing doctor',
      }),
    },
  ];

  const studyFields: FieldConfig[] = [
    {
      name: 'StudyDate',
      label: formatMessage({
        id: 'report.extendedConclusion.studyDate',
        defaultMessage: 'Study Date',
      }),
    },
    {
      name: 'TypeOfScan',
      label: formatMessage({
        id: 'report.extendedConclusion.typeOfScan',
        defaultMessage: 'Type of Scan',
      }),
    },
    {
      name: 'FOV',
      label: formatMessage({
        id: 'report.extendedConclusion.fov',
        defaultMessage: 'FOV',
      }),
    },
    {
      name: 'ScanSource',
      label: formatMessage({
        id: 'report.extendedConclusion.scanSource',
        defaultMessage: 'Scan Source',
      }),
    },
    {
      name: 'CBCTModel',
      label: formatMessage({
        id: 'report.extendedConclusion.cbctModel',
        defaultMessage: 'CBCT Model',
      }),
    },
    {
      name: 'IndicationForScan',
      label: formatMessage({
        id: 'report.extendedConclusion.indicationForScan',
        defaultMessage: 'Indication for Scan / Referral Notes',
      }),
    },
    {
      name: 'LimitationsFactors',
      label: formatMessage({
        id: 'report.extendedConclusion.limitationsFactors',
        defaultMessage:
          'Limitations or Technical Factors During Image Acquisition',
      }),
    },
  ];

  const observationFields: FieldConfig[] = [
    {
      name: 'Observations',
      label: formatMessage({
        id: 'report.extendedConclusion.observations',
        defaultMessage: 'OBSERVATIONS',
      }),
      isTitle: true,
    },
    {
      name: 'Dentition',
      label: formatMessage({
        id: 'report.extendedConclusion.dentition',
        defaultMessage: 'DENTITION',
      }),
    },
    {
      name: 'AreaOfInterest',
      label: formatMessage({
        id: 'report.extendedConclusion.areaOfInterest',
        defaultMessage: 'AREA OF INTEREST',
      }),
    },
    {
      name: 'Sinuses',
      label: formatMessage({
        id: 'report.extendedConclusion.sinuses',
        defaultMessage: 'SINUSES',
      }),
    },
    {
      name: 'Airway',
      label: formatMessage({
        id: 'report.extendedConclusion.airway',
        defaultMessage: 'AIRWAY',
      }),
    },
    {
      name: 'Osseous',
      label: formatMessage({
        id: 'report.extendedConclusion.osseous',
        defaultMessage: 'OSSEOUS',
      }),
    },
    {
      name: 'Additional',
      label: formatMessage({
        id: 'report.extendedConclusion.additional',
        defaultMessage: 'ADDITIONAL',
      }),
    },
  ];

  const impressionFields: FieldConfig[] = [
    {
      name: 'Impressions',
      label: formatMessage({
        id: 'report.extendedConclusion.impressions',
        defaultMessage: 'IMPRESSIONS',
      }),
      isTitle: true,
    },
  ];

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn(styles.content, className, isPreview && styles.preview)}
    >
      <div className={cn(styles.wrap)}>
        {patientFields.map((field, index) => (
          <Controller
            key={index}
            control={control}
            name={field.name}
            render={({ field: { value, onChange } }) => (
              <ConclusionInfoRow
                label={field.label}
                value={value}
                onChange={onChange}
                isEditMode={isEditMode}
                isTitle={field.isTitle}
                isSpan={field.isSpan}
                isReadOnly={field.name === 'Patient' || field.name === 'DOB'}
                isPreview={isPreview}
              />
            )}
          />
        ))}
      </div>
      <div className={cn(styles.wrap, styles.twoColumn)}>
        {studyFields.map((field, index) => (
          <Controller
            key={index}
            control={control}
            name={field.name}
            render={({ field: { value, onChange } }) => (
              <ConclusionInfoRow
                label={field.label}
                value={value}
                onChange={onChange}
                isEditMode={isEditMode}
                isTitle={field.isTitle}
                isSpan={field.isSpan}
                isPreview={isPreview}
              />
            )}
          />
        ))}
      </div>
      <div className={cn(styles.wrap)}>
        {observationFields.map((field, index) => (
          <Controller
            key={index}
            control={control}
            name={field.name}
            render={({ field: { value, onChange } }) => (
              <ConclusionInfoRow
                label={field.label}
                value={value}
                onChange={onChange}
                isEditMode={isEditMode}
                isTitle={field.isTitle}
                isSpan={field.isSpan}
                isPreview={isPreview}
              />
            )}
          />
        ))}
      </div>
      <div className={cn(styles.wrap)}>
        {impressionFields.map((field, index) => (
          <Controller
            key={index}
            control={control}
            name={field.name}
            render={({ field: { value, onChange } }) => (
              <ConclusionInfoRow
                label={field.label}
                value={value}
                onChange={onChange}
                isEditMode={isEditMode}
                isTitle={field.isTitle}
                isSpan={field.isSpan}
                isPreview={isPreview}
              />
            )}
          />
        ))}
      </div>

      {!isPreview && (
        <div className={styles.actions}>
          {isEditMode ? (
            <>
              <Button
                size="small"
                variant="tertiary"
                onClick={handleCancel}
                type="button"
              >
                <FormattedMessage id="global.cancel" defaultMessage="Cancel" />
              </Button>
              <Button
                size="small"
                variant="secondary"
                type="submit"
                loading={loading}
              >
                <FormattedMessage id="global.save" defaultMessage="Save" />
              </Button>
            </>
          ) : (
            <Button
              size="small"
              variant="secondary"
              onClick={handleEditStart}
              type="button"
            >
              <FormattedMessage id="global.edit" defaultMessage="Edit" />
            </Button>
          )}
        </div>
      )}
    </form>
  );
};
